import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../components/atoms/Input';
import Icon from '../components/atoms/Icon';
import SessionManager from '../utils/sessionManager';
import { login as loginRequest } from '../services/api';

const SaaSLoginPage = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const handleLogin = async (event) => {
    event.preventDefault();
    setFeedback({ type: '', message: '' });
    setIsSubmitting(true);

    try {
      const response = await loginRequest({
        email,
        password,
        role: 'saas_provider',
      });

      SessionManager.setSession('saas_provider', response);

      setFeedback({
        type: 'success',
        message: 'Welcome to SaaS Provider Dashboard!',
      });

      setTimeout(() => {
        navigate('/saas-provider/dashboard');
      }, 800);
    } catch (error) {
      setFeedback({
        type: 'error',
        message: error.message || 'Unable to log in. Please check your credentials.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#f1eef6]">

      {/* ================= LEFT SECTION ================= */}
      <div className="hidden lg:flex flex-col justify-center pl-28 relative">

        {/* === HEADING === */}
        <h1 className="text-[64px] font-extrabold leading-[1.1] tracking-tight text-center">
          {/* LINE 1 */}
          <span className="block text-[#2b0f4b] mb-2">
            SaaS{' '}
            <span className="bg-gradient-to-r from-[#ff5fa2] to-[#8b5cf6] bg-clip-text text-transparent">
              Provider
            </span>
          </span>
          {/* LINE 2 */}
          <span className="block mb-2">
            <span className="bg-gradient-to-r from-[#ffb347] to-[#ff5fa2] bg-clip-text text-transparent">
              Manage
            </span>{' '}
            <span className="bg-gradient-to-r from-[#ff5fa2] to-[#8b5cf6] bg-clip-text text-transparent">
              Your
            </span>
          </span>
          {/* LINE 3 */}
          <span className="flex justify-center">
            <span className="bg-[#2b0f4b] px-6 py-2 rounded-md">
              <span className="bg-gradient-to-r from-[#ff5fa2] to-[#8b5cf6] bg-clip-text text-transparent">
                Services
              </span>
            </span>
          </span>
        </h1>

        <div className="flex justify-center relative">
          <div className="mt-14 text-center">
            <p className="text-lg text-[#5b556f] font-medium">
              Access your SaaS management tools
            </p>
            <p className="text-sm text-[#8b5cf6] mt-2">
              Manage services • Track subscriptions • Analytics
            </p>
          </div>
        </div>
      </div>

      {/* ================= RIGHT SECTION ================= */}
      <div className="flex items-center justify-start pl-20">
        <div className="w-full max-w-md">

          <div className="text-center mb-6">
            <h2 className="text-[40px] font-bold text-[#2b0f4b] mb-2">
              SaaS Provider
            </h2>
            <p className="text-[#5b556f] text-sm">
              Sign in to manage your SaaS products and services
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">

            {/* Email / Username */}
            <div>
              <label className="text-base font-medium text-[#5b556f]">
                Email / Username
              </label>
              <Input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-12 mt-2 rounded-lg border border-[#d6d3e0]"
                placeholder="Enter your email or username"
                required
              />
            </div>

            {/* Password */}
            <div>
              <label className="text-base font-medium text-[#5b556f]">
                Password
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-12 mt-2 pr-12 rounded-lg border border-[#d6d3e0]"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9ca3af]"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <Icon name={showPassword ? 'visibility_off' : 'visibility'} />
                </button>
              </div>
            </div>

            {/* Feedback */}
            {feedback.message && (
              <div
                className={`text-sm px-4 py-3 rounded-lg ${
                  feedback.type === 'error'
                    ? 'bg-red-50 text-red-700 border border-red-200'
                    : 'bg-green-50 text-green-700 border border-green-200'
                }`}
              >
                {feedback.message}
              </div>
            )}

            {/* Login Button */}
            <div className="flex flex-col space-y-4">
              <button
                type="submit"
                disabled={!email || !password || isSubmitting}
                className="w-full px-12 py-3 bg-gradient-to-r from-[#7c3aed] to-[#a855f7] text-white font-bold uppercase tracking-wider rounded-full shadow-[0_8px_24px_rgba(124,58,237,0.45)] hover:from-[#6d28d9] hover:to-[#9333ea] hover:shadow-[0_10px_28px_rgba(124,58,237,0.6)] active:scale-95 disabled:from-[#7c3aed]/70 disabled:to-[#a855f7]/70 disabled:cursor-not-allowed transition-all duration-200"
                style={{
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.25), 0 8px 24px rgba(124,58,237,0.45)',
                }}
              >
                {isSubmitting ? 'SIGNING IN...' : 'SIGN IN TO DASHBOARD'}
              </button>
              
              {/* Back to main site */}
              <button
                type="button"
                onClick={() => navigate('/')}
                className="text-base text-[#8b5cf6] hover:text-[#7c3aed] transition-colors font-medium text-center"
              >
                ← Back to Main Site
              </button>
            </div>
          </form>

          {/* Contact Support */}
          <div className="mt-8 text-center">
            <p className="text-sm text-[#5b556f]">
              Need help accessing your account?{' '}
              <button
                onClick={() => navigate('/forgot-password')}
                className="text-[#8b5cf6] hover:text-[#7c3aed] font-medium"
              >
                Reset Password
              </button>
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default SaaSLoginPage;
